// src/components/common/Filters.tsx
import { Search, Download, Trash2, RefreshCw } from "lucide-react";
import type { DateRange } from 'react-day-picker'
import { useTranslation } from "react-i18next";

import {
    Button, Card, CardContent, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
    Label,
} from "@/components/ui";
import { DateRangePicker } from '@/components/ui/date-picker'
import { Slider } from "@/components/ui/slider";

interface FilterOption {
    value: string;
    label: string;
}

interface RangeFilter {
    name: string;
    label: string;
    type: "range";
    value: { min: number; max: number };
    onChange: (value: { min: number; max: number }) => void;
    min: number;
    max: number | (() => number);  // max can be a number or a function that returns a number
    step: number;
}

interface SelectFilter {
    name: string;
    value: string;
    options: FilterOption[];
    onChange: (value: string) => void;
}

interface DateRangeFilter {
    name: string;
    label: string;
    type: 'dateRange';
    value: DateRange | undefined;
    onChange: (value: DateRange | undefined) => void;
}

type Filter = SelectFilter | RangeFilter | DateRangeFilter;

interface FiltersProps {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    filters: Filter[];
    selectedCount?: number;
    onBulkDelete?: () => void;
    onExport?: () => void;
    onRefresh?: () => void;
}

export function Filters({
    searchQuery,
    onSearchChange,
    filters,
    selectedCount = 0,
    onBulkDelete,
    onExport,
    onRefresh,
}: FiltersProps) {
    const { t } = useTranslation();

    const getMaxValue = (filter: RangeFilter): number => {
        return typeof filter.max === 'function' ? filter.max() : filter.max;
    };

    return (
        <Card className="p-0">
            <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    <div className="md:col-span-9 flex gap-6 flex-wrap">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px] max-w-[300px]">
                            <Label className="text-xs text-muted-foreground mb-1">{t("common.search")}</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    className="pl-9"
                                    value={searchQuery}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* DateRange Filters */}
                        {filters.filter((filter): filter is DateRangeFilter => 'type' in filter && filter.type === 'dateRange').map((filter) => (
                            <div key={filter.name} className="min-w-[260px]">
                                <Label className="text-xs text-muted-foreground mb-1">{filter.label}</Label>
                                <DateRangePicker
                                    selected={filter.value}
                                    onSelect={(range) => {
                                        if (!range || (typeof range === 'object' && 'from' in range)) {
                                            filter.onChange(range as DateRange | undefined)
                                        }
                                    }}
                                />
                            </div>
                        ))}

                        {/* Select Filters */}
                        {filters.filter((filter): filter is SelectFilter => !('type' in filter)).map((filter) => (
                            <div key={filter.name} className="min-w-[180px]">
                                <Label className="text-xs text-muted-foreground mb-1">{t(`filter.${filter.name}`)}</Label>
                                <Select
                                    value={filter.value}
                                    onValueChange={filter.onChange}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={t(`filter.${filter.name}`)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            {t(`filter.all.${filter.name}`)}
                                        </SelectItem>
                                        {filter.options.map((option) => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}

                        {/* Range Filters */}
                        {filters.filter((filter): filter is RangeFilter => 'type' in filter && filter.type === 'range').map((filter) => {
                            const maxValue = getMaxValue(filter);
                            return (
                                <div key={filter.name} className="min-w-[160px]">
                                    <Label className="text-xs text-muted-foreground mb-1">{filter.label}</Label>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <div>
                                            <Input
                                                type="number"
                                                value={filter.value.min}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    if (value >= filter.min && value <= filter.value.max) {
                                                        filter.onChange({ ...filter.value, min: value });
                                                    }
                                                }}
                                                min={filter.min}
                                                max={filter.value.max}
                                                step={filter.step}
                                                className="h-8"
                                            />
                                        </div>
                                        <div>
                                            <Input
                                                type="number"
                                                value={filter.value.max}
                                                onChange={(e) => {
                                                    const value = Number(e.target.value);
                                                    if (value >= filter.value.min && value <= maxValue) {
                                                        filter.onChange({ ...filter.value, max: value });
                                                    }
                                                }}
                                                min={filter.value.min}
                                                max={maxValue}
                                                step={filter.step}
                                                className="h-8"
                                            />
                                        </div>
                                    </div>
                                    <Slider
                                        value={[filter.value.min, filter.value.max]}
                                        onValueChange={([min, max]: [number, number]) => filter.onChange({ min, max })}
                                        min={filter.min}
                                        max={maxValue}
                                        step={filter.step}
                                        className="mt-1"
                                    />
                                </div>
                            );
                        })}
                    </div>

                    <div className="md:col-span-3 flex justify-end gap-2">
                        {onExport && (
                            <Button variant="outline" onClick={onExport}>
                                <Download className="h-4 w-4" strokeWidth={1.5} />
                                <span className="">{t("common.export")}</span>
                            </Button>
                        )}
                        {onRefresh && (
                            <Button variant="outline" onClick={onRefresh}>
                                <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                                <span className="">{t("common.refresh")}</span>
                            </Button>
                        )}
                        {onBulkDelete && selectedCount > 0 && (
                            <Button
                                variant="destructive"
                                onClick={onBulkDelete}
                            >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                <span className="">{t("common.delete")}</span>
                            </Button>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}